import { 
  PatientRecord, 
  KPIMetric, 
  REGIONS, 
  AGE_GROUPS, 
  formatCurrency, 
  getBMICategory 
} from '@/lib/index';

const NAMES = [
  'James Smith', 'Maria Garcia', 'Robert Johnson', 'Patricia Miller', 'Michael Brown', 
  'Linda Davis', 'William Rodriguez', 'Elizabeth Martinez', 'David Hernandez', 'Barbara Lopez',
  'Richard Wilson', 'Susan Anderson', 'Joseph Taylor', 'Jessica Thomas', 'Thomas Moore',
  'Sarah Jackson', 'Charles Martin', 'Karen Lee', 'Christopher Perez', 'Nancy Thompson'
];

const TREATMENT_TYPES: PatientRecord['treatmentType'][] = ['Inpatient', 'Outpatient', 'Emergency', 'Routine'];
const STATUSES: PatientRecord['status'][] = ['Stable', 'Critical', 'Recovering', 'Follow-up'];

const generateMockPatients = (count: number): PatientRecord[] => {
  const patients: PatientRecord[] = [];
  for (let i = 0; i < count; i++) {
    const age = Math.floor(Math.random() * (85 - 18) + 18);
    const smoker = Math.random() > 0.8 ? 'yes' : 'no';
    const bmi = Math.random() * (45 - 16) + 16;
    const children = Math.floor(Math.random() * 5);
    const region = REGIONS[Math.floor(Math.random() * REGIONS.length)];
    
    // Base charges + multipliers for risk factors
    let baseCharges = age * 250;
    if (smoker === 'yes') baseCharges *= 3.5;
    if (bmi > 30) baseCharges *= 1.4;
    const charges = baseCharges + (Math.random() * 5000);

    const riskScore = Math.min(100, Math.floor(
      (age / 85 * 30) + 
      (smoker === 'yes' ? 40 : 0) + 
      (bmi > 30 ? 20 : 0) + 
      (Math.random() * 10)
    ));

    const lastVisitDate = new Date(2026, 1, Math.floor(Math.random() * 17) + 1);

    patients.push({
      id: `PAT-${1000 + i}`,
      name: NAMES[i % NAMES.length],
      age,
      sex: Math.random() > 0.5 ? 'male' : 'female',
      bmi,
      children,
      smoker,
      region,
      charges,
      lastVisit: lastVisitDate.toISOString().split('T')[0],
      status: riskScore > 80 ? 'Critical' : riskScore > 50 ? 'Follow-up' : Math.random() > 0.5 ? 'Stable' : 'Recovering',
      treatmentType: TREATMENT_TYPES[Math.floor(Math.random() * TREATMENT_TYPES.length)],
      riskScore
    });
  }
  return patients;
};

export const patientData = generateMockPatients(100);

export const kpiMetrics: KPIMetric[] = [
  {
    title: 'Total Patients',
    value: patientData.length,
    change: 12,
    trend: 'up',
    icon: 'Users',
    description: 'Active patients in the current period',
    color: 'oklch(0.52 0.18 255)'
  },
  {
    title: 'Avg. Treatment Cost',
    value: formatCurrency(patientData.reduce((acc, p) => acc + p.charges, 0) / patientData.length),
    change: -5.2,
    trend: 'down',
    icon: 'DollarSign',
    description: 'Average expenditure per patient record',
    color: 'oklch(0.68 0.16 165)'
  },
  {
    title: 'Critical Cases',
    value: patientData.filter(p => p.status === 'Critical').length,
    change: 8.4,
    trend: 'up',
    icon: 'Activity',
    description: 'High-risk patients requiring immediate attention',
    color: 'oklch(0.55 0.18 28)'
  },
  {
    title: 'Recovery Rate',
    value: '84.2%',
    change: 2.1,
    trend: 'up',
    icon: 'HeartPulse',
    description: 'Percentage of patients moved to recovering status',
    color: 'oklch(0.65 0.15 190)'
  }
];

export const regionalStats = REGIONS.map(region => ({
  name: region.charAt(0).toUpperCase() + region.slice(1),
  patients: patientData.filter(p => p.region === region).length,
  charges: Math.floor(patientData.filter(p => p.region === region).reduce((acc, p) => acc + p.charges, 0)),
  avgRisk: Math.floor(patientData.filter(p => p.region === region).reduce((acc, p) => acc + p.riskScore, 0) / 
            patientData.filter(p => p.region === region).length)
}));

export const ageDistribution = AGE_GROUPS.map(group => {
  let count = 0;
  if (group === '65+') {
    count = patientData.filter(p => p.age >= 65).length;
  } else {
    const [min, max] = group.split('-').map(Number);
    count = patientData.filter(p => p.age >= min && p.age <= max).length;
  }
  return { group, count };
});

export const costAnalysis = [
  { month: 'Sep 2025', inpatient: 45000, outpatient: 32000, emergency: 12000 },
  { month: 'Oct 2025', inpatient: 42000, outpatient: 35000, emergency: 15000 },
  { month: 'Nov 2025', inpatient: 48000, outpatient: 31000, emergency: 18000 },
  { month: 'Dec 2025', inpatient: 52000, outpatient: 38000, emergency: 22000 },
  { month: 'Jan 2026', inpatient: 49000, outpatient: 41000, emergency: 19000 },
  { month: 'Feb 2026', inpatient: 55000, outpatient: 43000, emergency: 25000 }
];

export const chartData = {
  bmiVsCharges: patientData.map(p => ({
    bmi: parseFloat(p.bmi.toFixed(1)),
    charges: Math.floor(p.charges),
    age: p.age,
    smoker: p.smoker,
    category: getBMICategory(p.bmi)
  })),
  genderDistribution: [
    { name: 'Male', value: patientData.filter(p => p.sex === 'male').length },
    { name: 'Female', value: patientData.filter(p => p.sex === 'female').length }
  ],
  smokerRisk: [
    { name: 'Smoker', avgRisk: Math.floor(patientData.filter(p => p.smoker === 'yes').reduce((acc, p) => acc + p.riskScore, 0) / patientData.filter(p => p.smoker === 'yes').length) },
    { name: 'Non-Smoker', avgRisk: Math.floor(patientData.filter(p => p.smoker === 'no').reduce((acc, p) => acc + p.riskScore, 0) / patientData.filter(p => p.smoker === 'no').length) }
  ]
};